import { __decorate, __metadata } from "tslib";
import { CacheActivePromise } from '@1inch-community/core/decorators';
import { lazyAppContext } from '@1inch-community/core/lazy';
import { BigFloat } from '@1inch-community/core/math';
import { ChainId } from '@1inch-community/models';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, from, fromEvent, map, merge, of, shareReplay, startWith, switchMap, take, timer, } from 'rxjs';
import { createPublicClient, parseAbi, } from 'viem';
import { buildTokenId } from '../tokens';
import { averageBlockTime } from './average-block-time';
import { BlockTimeCache } from './block-time-cache';
import { getWrapperNativeTokenAddress } from './contracts';
import { isNativeToken } from './is-native-token';
import { WebFallbackTransportController } from './transport/web-fallback-transport.controller';
import { getChainById } from './viem-chain-map';
const abi = parseAbi([
    'function allowance(address owner, address spender) public view returns (uint256)',
    'function approve(address _spender, uint256 _value) public returns (bool success)',
    'function deposit() public payable',
]);
export class OnChainManager {
    constructor() {
        this.context = lazyAppContext('OnChainManager');
        this.clientMap = new Map();
        this.blockEmitterMap = new Map();
        this.chainTickEmitterMap = new Map();
        this.allowanceCache = new BlockTimeCache();
    }
    get crossChainEmitter() {
        return this.getChainTickEmitter(ChainId.eth);
    }
    async init(context) {
        this.context.set(context);
    }
    getBlockEmitter(chainId) {
        if (this.blockEmitterMap.has(chainId)) {
            return this.blockEmitterMap.get(chainId);
        }
        return this.buildBlockEmitter(chainId);
    }
    getChainTickEmitter(chainId) {
        if (this.chainTickEmitterMap.has(chainId)) {
            return this.chainTickEmitterMap.get(chainId);
        }
        return this.chainTickEmitterEmitter(chainId);
    }
    async getClient(chainId) {
        if (this.clientMap.has(chainId)) {
            return this.clientMap.get(chainId).client;
        }
        return await this.buildClient(chainId);
    }
    async estimateWrapNativeToken(chainId, value) {
        const client = await this.getClient(chainId);
        const address = getWrapperNativeTokenAddress(chainId);
        const result = await client.estimateContractGas({
            abi,
            address,
            value: value.toBigInt(18),
            functionName: 'deposit',
        });
        return BigFloat.fromBigInt(result, 18);
    }
    async simulateWrapNativeToken(chainId, value) {
        const client = await this.getClient(chainId);
        const address = getWrapperNativeTokenAddress(chainId);
        const result = await client.simulateContract({
            abi,
            address,
            value: value.toBigInt(18),
            functionName: 'deposit',
        });
        return result.request;
    }
    async waitTransaction(chainId, hash, blockTag = 'latest') {
        const client = await this.getClient(chainId);
        const stream = this.getBlockEmitter(chainId).pipe(switchMap(async () => {
            const tx = await client.getTransaction({ hash, blockTag });
            if (tx.blockNumber) {
                return tx;
            }
            return null;
        }), filter((tx) => tx !== null), take(1));
        return firstValueFrom(stream);
    }
    async getAllowance(chainId, tokenAddress, owner, spender) {
        if (isNativeToken(tokenAddress)) {
            return BigFloat.maxUint256();
        }
        const id = [chainId, tokenAddress, owner, spender].join(':');
        const cachedValue = this.allowanceCache.get(chainId, id);
        if (cachedValue !== null) {
            return cachedValue;
        }
        const tokenRecordId = buildTokenId(chainId, tokenAddress);
        const token = await this.context.value.tokenStorage.getTokenById({ tokenRecordId });
        if (!token)
            return BigFloat.zero();
        const client = await this.getClient(chainId);
        const result = await client.readContract({
            abi,
            functionName: 'allowance',
            args: [owner, spender],
            address: tokenAddress,
        });
        const allowance = BigFloat.fromBigInt(result, token.decimals);
        this.allowanceCache.set(chainId, id, allowance);
        return allowance;
    }
    async simulateApprove(chainId, tokenAddress, owner, spender, value) {
        if (isNativeToken(tokenAddress)) {
            throw new Error('Native token in not supported approve');
        }
        const tokenRecordId = buildTokenId(chainId, tokenAddress);
        const token = await this.context.value.tokenStorage.getTokenById({ tokenRecordId });
        if (!token)
            throw new Error('Token not found');
        const client = await this.getClient(chainId);
        const result = await client.simulateContract({
            abi,
            account: owner,
            functionName: 'approve',
            args: [spender, value.toBigInt(token.decimals)],
            address: tokenAddress,
        });
        return result.request;
    }
    async buildClient(chainId) {
        if (!this.context)
            throw new Error('No context provided');
        const chain = getChainById(chainId);
        const transportController = new WebFallbackTransportController(chain);
        await transportController.init(this.context.value);
        const client = createPublicClient({ chain, transport: transportController.createTransport() });
        this.clientMap.set(chainId, { client, transportController });
        await transportController.benchMartTransport();
        return client;
    }
    buildBlockEmitter(chainId) {
        const updateTime$ = buildUpdateTime(chainId);
        const client$ = from(this.getClient(chainId));
        const block$ = combineLatest([client$, updateTime$]).pipe(switchMap(([client, time]) => blockListener(client, time)), distinctUntilChanged((b1, b2) => b1.number === b2.number), shareReplay({ bufferSize: 1, refCount: true }));
        this.blockEmitterMap.set(chainId, block$);
        return block$;
    }
    chainTickEmitterEmitter(chainId) {
        const tick$ = buildUpdateTime(chainId).pipe(switchMap((time) => {
            if (time === null)
                return of(void 0);
            return timer(0, time).pipe(map(() => void 0));
        }), shareReplay({ bufferSize: 1, refCount: true }));
        this.chainTickEmitterMap.set(chainId, tick$);
        return tick$;
    }
}
__decorate([
    CacheActivePromise(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number]),
    __metadata("design:returntype", Promise)
], OnChainManager.prototype, "getClient", null);
__decorate([
    CacheActivePromise(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, Object]),
    __metadata("design:returntype", Promise)
], OnChainManager.prototype, "estimateWrapNativeToken", null);
__decorate([
    CacheActivePromise(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, Object]),
    __metadata("design:returntype", Promise)
], OnChainManager.prototype, "simulateWrapNativeToken", null);
__decorate([
    CacheActivePromise(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, String, String]),
    __metadata("design:returntype", Promise)
], OnChainManager.prototype, "waitTransaction", null);
__decorate([
    CacheActivePromise(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, String, String, String]),
    __metadata("design:returntype", Promise)
], OnChainManager.prototype, "getAllowance", null);
__decorate([
    CacheActivePromise(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, String, String, String, Object]),
    __metadata("design:returntype", Promise)
], OnChainManager.prototype, "simulateApprove", null);
function buildUpdateTime(chainId) {
    return combineLatest([
        isWindowVisibleAndFocused$().pipe(map((state) => (state ? averageBlockTime[chainId] : null))),
        sleepOnMousemove$().pipe(map((state) => (state ? 30 * 1000 : null))),
    ]).pipe(map(([time1, time2]) => time1 ?? time2));
}
function isWindowVisibleAndFocused$() {
    let isFirst = true;
    return merge(fromEvent(window, 'focus'), fromEvent(window, 'blur'), fromEvent(document, 'visibilitychange')).pipe(startWith(null), map(() => {
        if (isFirst) {
            isFirst = false;
            return true;
        }
        return isWindowVisibleAndFocused();
    }), distinctUntilChanged());
}
function isWindowVisibleAndFocused() {
    const isFocused = document.hasFocus();
    const isVisible = document.visibilityState === 'visible';
    return isFocused && isVisible;
}
function blockListener(client, time) {
    if (time === null) {
        return from(client.getBlock());
    }
    return timer(0, time).pipe(switchMap(() => client.getBlock()));
}
function sleepOnMousemove$() {
    return fromEvent(window, 'mousemove').pipe(startWith(null), switchMap(() => timer(10 * 1000).pipe(map(() => true), startWith(false))), distinctUntilChanged());
}
//# sourceMappingURL=chain.manager.js.map